/* eslint-disable @typescript-eslint/no-unused-vars, no-unused-vars */
import React from 'react';
import { Box, Typography, IconButton, Button, Chip } from '@mui/material';
import { ArrowLeft } from 'lucide-react';
import { LAYOUT } from '../../../theme';

interface FormPageLayoutProps {
  title: React.ReactNode;
  onCancel: () => void;
  onSave: () => void;
  saveButtonText: string;
  isFormValid: boolean;
  hasUnsavedChanges?: boolean;
  isSaving?: boolean;
  children: React.ReactNode;
}

export const FormPageLayout: React.FC<FormPageLayoutProps> = ({
  title,
  onCancel,
  onSave,
  saveButtonText,
  isFormValid,
  hasUnsavedChanges = true,
  isSaving = false,
  children,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
        maxWidth: LAYOUT.mediumContentWidth,
        mx: 'auto',
        pb: 12, // Space for fixed bottom actions
        pt: 2,
        px: 2,
      }}
    >
      {/* Header with back button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <IconButton onClick={onCancel} sx={{ p: 1 }}>
          <ArrowLeft />
        </IconButton>
        <Typography variant="h5" component="h1">
          {title}
        </Typography>
      </Box>

      {/* Form content */}
      {children}

      {/* Fixed bottom actions */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 2,
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          py: 2,
          px: 3,
          zIndex: 1000,
        }}
      >
        {/* Back button on the left */}
        <IconButton onClick={onCancel} sx={{ p: 1 }} title="Go back">
          <ArrowLeft size={20} />
        </IconButton>

        {/* Unsaved changes indicator in the center */}
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          {hasUnsavedChanges && !isSaving && (
            <Chip
              label="Unsaved changes"
              sx={{
                bgcolor: '#ed6c02',
                color: 'white',
                fontWeight: 500,
                fontSize: '0.875rem',
              }}
            />
          )}
        </Box>

        {/* Action buttons on the right */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={!isFormValid || isSaving}
            sx={{ minWidth: 120 }}
          >
            {isSaving ? 'Saving...' : saveButtonText}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
